import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Grid,
  Typography,
  Box,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import api from "../utilities/axios";
import { useMessage } from "../components/Header";
import { useOrganization } from "../hooks/useOrganization";

export default function AdminSettingsModal({ open, onClose }) {
  const { selectedOrganization, selectOrganization } = useOrganization();
  const { showSuccess, showError } = useMessage();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [smtpConfig, setSmtpConfig] = useState({
    host: "",
    port: "",
    user: "",
    pass: "",
    secure: true,
    useCustomSMTP: false,
  });

  useEffect(() => {
    if (open && selectedOrganization) {
      // If organization has existing config, populate it
      if (selectedOrganization.smtpConfig) {
        setSmtpConfig({
          host: selectedOrganization.smtpConfig.host || "",
          port: selectedOrganization.smtpConfig.port || "",
          user: selectedOrganization.smtpConfig.user || "",
          pass: selectedOrganization.smtpConfig.pass || "",
          secure: selectedOrganization.smtpConfig.secure ?? true,
          useCustomSMTP: selectedOrganization.smtpConfig.useCustomSMTP ?? false,
        });
      } else {
        // Reset to default if no config
        setSmtpConfig({
          host: "",
          port: "",
          user: "",
          pass: "",
          secure: true,
          useCustomSMTP: false,
        });
      }
    }
  }, [open, selectedOrganization]);

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setSmtpConfig((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSave = async () => {
    if (!selectedOrganization?._id) return;
    
    setSaving(true);
    try {
      const platformUser = JSON.parse(localStorage.getItem("user")) || {};
      const payload = {
        ...smtpConfig,
        recoveryEmail: platformUser.recoveryEmail || platformUser.email,
        firstName: platformUser.firstName || "User"
      };

      const response = await api.post(`/user/organizations/${selectedOrganization._id}/smtp`, payload);
      if (response.data.success) {
        showSuccess("SMTP settings verified and updated successfully!");
        // Update organization in context
        selectOrganization(response.data.organization);
        onClose();
      } else {
        showError(response.data.message || "Failed to update SMTP settings");
      }
    } catch (error) {
      console.error("Error updating SMTP settings:", error);
      const backendError = error.response?.data;
      if (backendError?.message === "SMTP Verification Failed") {
        showError(
          <Box>
            <Typography variant="body2" fontWeight="bold">{backendError.message}</Typography>
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>{backendError.error}</Typography>
            <Typography variant="caption" display="block" color="primary" sx={{ mt: 1 }}>{backendError.suggestion}</Typography>
          </Box>
        );
      } else {
        showError(backendError?.message || "An error occurred while saving");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Admin Settings - {selectedOrganization?.name}</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box mb={3}>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
            Email Delivery Settings
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={smtpConfig.useCustomSMTP}
                onChange={handleChange}
                name="useCustomSMTP"
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body1">Use Custom SMTP</Typography>
                <Typography variant="caption" color="textSecondary">
                  When enabled, emails for this organization will be sent using these credentials instead of {process.env.REACT_APP_NAME} sender.
                </Typography>
              </Box>
            }
          />
        </Box>

        {smtpConfig.useCustomSMTP && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="SMTP Host"
                name="host"
                value={smtpConfig.host}
                onChange={handleChange}
                placeholder="smtp.gmail.com"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Port"
                name="port"
                type="number"
                value={smtpConfig.port}
                onChange={handleChange}
                placeholder="465"
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="SMTP User / Email"
                name="user"
                value={smtpConfig.user}
                onChange={handleChange}
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="SMTP Password"
                name="pass"
                type="password"
                value={smtpConfig.pass}
                onChange={handleChange}
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={smtpConfig.secure}
                    onChange={handleChange}
                    name="secure"
                    color="primary"
                  />
                }
                label="Use Secure Connection (SSL/TLS)"
              />
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={saving}
          startIcon={saving && <CircularProgress size={20} />}
        >
          {saving ? "Saving..." : "Save Settings"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
